import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDate, formatEatTime } from '@wodi/domain';
import type { LabelChange, LabelRef, VisitSensitivityView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Select,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import { changeVisitLabel, privacyKeys, visitSensitivityQuery } from '../api/privacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import { errorText } from './labels-settings-page.tsx';
import styles from './privacy.module.css';

// A visit's sensitivity labels, from the consultation (docs/plans/golive-privacy-tooling.md
// §1.4). Putting a label on keeps the visit to the roles cleared for it; everyone else sees it
// as not found, or as a restricted record where care or payment would otherwise stop. A label
// is put on or taken off with a reason. TODO(clinical-verify) P1: who may do each.

const REASON_MIN = 5;

function History({ view }: { view: VisitSensitivityView }) {
  if (view.history.length === 0) return null;
  return (
    <>
      <h3 className={styles.meta}>History</h3>
      <ul className={styles.history} aria-label="Label history">
        {view.history.map((h) => {
          const at = new Date(h.at);
          return (
            <li key={h.id} className={styles.meta}>
              {`${formatEatDate(at)} ${formatEatTime(at)}: ${h.label.display} ${h.change === 'applied' ? 'put on' : 'taken off'} by ${h.by.displayName}. ${h.reason}`}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function LabelsDialog({ visitId, onClose }: { visitId: string; onClose: () => void }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const view = useQuery(visitSensitivityQuery(visitId));
  const [labelId, setLabelId] = useState('');
  const [reason, setReason] = useState('');
  const mayApply = session !== null && can(session, 'visits.label');
  const mayRemove = session !== null && can(session, 'visits.unlabel');
  const reasonReady = reason.trim().length >= REASON_MIN;
  const mutation = useMutation({
    mutationFn: ({ change, label }: { change: LabelChange; label: LabelRef }) =>
      changeVisitLabel(visitId, change, { labelId: label.id, reason: reason.trim() }).then(
        (next) => ({ next, change, label }),
      ),
    onSuccess: async ({ next, change, label }) => {
      queryClient.setQueryData(privacyKeys.visit(visitId), next);
      setLabelId('');
      setReason('');
      toasts.show({
        title:
          change === 'apply' ? `${label.display} label put on` : `${label.display} label taken off`,
      });
      // What other screens show of this visit depends on its labels.
      await queryClient.invalidateQueries({ queryKey: ['visits', visitId] });
    },
  });
  const chosen = view.data?.available.find((l) => l.id === labelId);
  return (
    <Dialog
      title="Sensitivity labels"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {view.isPending ? <LoadingBlock label="Loading the labels" rows={2} /> : null}
      {view.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void view.refetch()}>Try again</Button>}
        >
          {errorText(view.error, 'The labels could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {view.isSuccess ? (
        <>
          <p className={styles.meta}>
            Only people whose role is cleared for every label on this visit can open it.
          </p>
          {view.data.labels.length === 0 ? (
            <p>This visit has no labels.</p>
          ) : (
            <ul className={styles.history} aria-label="Labels on this visit">
              {view.data.labels.map((l) => (
                <li key={l.id} className={styles.label}>
                  <span>
                    <Tag state="attention">{l.display}</Tag>
                    {l.description === null ? null : (
                      <span className={styles.meta}>{` ${l.description}`}</span>
                    )}
                  </span>
                  {mayRemove ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online || !reasonReady || mutation.isPending}
                      onPress={() => {
                        mutation.mutate({ change: 'remove', label: l });
                      }}
                    >
                      Take off
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {mayApply ? (
            view.data.available.length === 0 ? (
              <p className={styles.meta}>
                No other label your role is cleared for. Ask an administrator if one is missing.
              </p>
            ) : (
              <Select
                label="Label to put on"
                options={view.data.available.map((l) => ({ id: l.id, label: l.display }))}
                value={labelId}
                onChange={(v) => {
                  setLabelId(typeof v === 'string' ? v : '');
                }}
                {...(chosen?.description == null ? {} : { description: chosen.description })}
              />
            )
          ) : null}
          {mayApply || mayRemove ? (
            <TextArea
              label="Reason"
              description="Needed to put a label on or take one off. Kept in the visit's label history."
              rows={2}
              value={reason}
              onChange={setReason}
            />
          ) : null}
          <History view={view.data} />
        </>
      ) : null}
      {!online ? (
        <Notice tone="info">No connection to Wodi. Label changes wait for the connection.</Notice>
      ) : null}
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The label was not changed.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Close
        </Button>
        {mayApply ? (
          <Button
            variant="primary"
            isDisabled={!online || chosen === undefined || !reasonReady}
            isPending={mutation.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              if (chosen !== undefined) mutation.mutate({ change: 'apply', label: chosen });
            }}
          >
            Put label on
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

/** "Sensitivity" for whoever may label or unlabel a visit; opens the labels dialog. */
export function VisitLabelsButton({ visitId }: { visitId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [open, setOpen] = useState(false);
  if (session === null || !(can(session, 'visits.label') || can(session, 'visits.unlabel')))
    return null;
  return (
    <>
      <Button
        variant="quiet"
        onPress={() => {
          setOpen(true);
        }}
      >
        Sensitivity
      </Button>
      {open ? (
        <LabelsDialog
          visitId={visitId}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
